import { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { difference, union } from 'lodash';
import { confirmAlert } from 'react-confirm-alert';

//components
import withLocalization from '../../../hoc/withLocalization';
import GenericList from '../../../components/GenericList/GenericList';
import GenericFilter from '../../../components/GenericList/GenericFilter';
import Button from '../../../components/CustomButton/CustomButton';
import Checkbox from '../../../components/CustomCheckbox/CustomCheckbox';

//elements
import LoadingSpinner from '../../../elements/LoadingSpinner';

//assets
import Delete from '../../../assets/img/deleting.svg';

const pm_assignment_statuses = [
    { code: 'assigned', name: 'Assigned' },
    { code: 'unassigned', name: 'Unassigned' },
];

class UserPmAssignForm extends Component {
    constructor(props) {
        super(props);
        this.state = {
            filters: {
                name: '',
                status: '',
            },
            appliedFilters: {
                name: '',
                status: '',
            },
            selectedPmIds: [],
            currentPageUnassignedIds: [],
            assignedCount: 0,
            unassignedCount: 0,
            loading: false,
            reloadKey: Date.now(),
            page: 0,
            isPageAddedOrEdited: false,
        };
    }

    componentDidMount() {
        this.loadData();
    }

    setFilter = (field, value) => {
        this.setState(prevState => ({
            filters: {
                ...prevState.filters,
                [field]: value
            }
        }));
    };

    handlePageChange = (page, isPageAddedOrEdited) => {
        this.setState({ page, isPageAddedOrEdited });
    };

    onFilter = () => {
        this.setState(prevState => ({
            appliedFilters: { ...prevState.filters },
            reloadKey: Date.now(),
            selectedPmIds: [],
            currentPageUnassignedIds: [],
            page: 0,
            isPageAddedOrEdited: false
        }));
    };

    updateStatusFilter = (status) => {
        const { appliedFilters } = this.state;
        const newStatus = appliedFilters.status === status ? '' : status;

        this.setState(prev => ({
            filters: { ...prev.filters, status: newStatus }
        }),
            this.onFilter);
    }

    loadData = async () => {
        const { userStore, id, updateManagerCount } = this.props;
        const { assigned, unassigned } = await userStore.pmsForUserCount({ userId: id });
        this.setState({ assignedCount: assigned, unassignedCount: unassigned });

        if (updateManagerCount) updateManagerCount(assigned || 0);
    }

    togglePmSelection = (id) => {
        this.setState(prevState => {
            const { selectedPmIds } = prevState;
            const newIds = selectedPmIds.includes(id)
                ? selectedPmIds.filter(itemId => itemId !== id)
                : [...selectedPmIds, id];

            return { selectedPmIds: newIds };
        });
    }

    toggleSelectAll = () => {
        this.setState(prevState => {
            const { currentPageUnassignedIds, selectedPmIds } = prevState;
            const allCurrentSelected = currentPageUnassignedIds.every(id => selectedPmIds.includes(id));

            if (allCurrentSelected) {
                return { selectedPmIds: difference(selectedPmIds, currentPageUnassignedIds) };
            }
            return { selectedPmIds: union(selectedPmIds, currentPageUnassignedIds) };
        });
    }

    fetchPms = (params = {}) => {
        const { name, status } = this.state.appliedFilters;
        const { userStore } = this.props;
        const options = {
            name: name || '',
            assigned: status || 'all',
            page: params.page || 0,
            pagesize: params.pagesize,
            isNotPagination: params.isNotPagination || false,
        };

        return userStore.getPmsForUser(this.props.id, options).then(response => {
            const list = response.rows || [];
            if (list.length > 0) {
                const unassignedIds = list.filter(pm => !pm.is_assigned).map(pm => pm.id);
                this.setState({ currentPageUnassignedIds: unassignedIds });
            }
            return response;
        });
    };

    assignSelectedPms = async () => {
        const { userStore, commonStore, t, id } = this.props;
        const { selectedPmIds } = this.state;

        if (selectedPmIds.length === 0) return;

        this.setState({ loading: true });

        try {
            await userStore.assignPmToUser(id, selectedPmIds);
            commonStore.addNotification(t('Saved'), null, 'success');
            this.setState({
                reloadKey: Date.now(),
                selectedPmIds: [],
                currentPageUnassignedIds: [],
                loading: false,
                isPageAddedOrEdited: true
            }, () => this.loadData());
        } catch (err) {
            this.setState({ loading: false });
            commonStore.addNotification(err.message || t('Error'), null, 'error');
        }
    };

    unassignPm = async (pmId) => {
        const { userStore, commonStore, t, id } = this.props;
        try {
            confirmAlert({
                title: `${t('Confirm')} ?`,
                message: t('Are you sure you want to remove this manager from the employee?'),
                buttons: [
                    {
                        label: t('Yes'),
                        onClick: async () => {
                            const res = await userStore.deleteUserPm(id, pmId);
                            commonStore.addNotification(t(res.message) || t('Saved'), null, 'success');
                            this.setState({ reloadKey: Date.now(), isPageAddedOrEdited: true }, () => this.loadData());
                        },
                    },
                    {
                        label: t('No'),
                        onClick: () => { }
                    },
                ]
            });
        } catch (err) {
            commonStore.addNotification(err.message || t('Error'), null, 'error');
        }
    };

    renderCheckboxCell = (row) => {
        const pmId = row.original.id;
        const isAssigned = !!row.original.is_assigned;
        const { selectedPmIds } = this.state;
        const isSelected = selectedPmIds.includes(pmId);

        return (
            <div className="d-flex align-items-center justify-content-center">
                {isAssigned ? (
                    <Button icon_sm fill onClick={() => this.unassignPm(pmId)} title={this.props.t('Unassign')}>
                        <img src={Delete} alt="delete" />
                    </Button>
                ) : (
                    <Checkbox
                        inlineBlock
                        isChecked={isSelected}
                        onChange={() => this.togglePmSelection(pmId)}
                        wrapperClassName="checkbox-position-fix"
                        labelClassName="ps-0"
                    />
                )}
            </div>
        );
    }

    render() {
        const { id, t } = this.props;
        const { filters, appliedFilters, loading, selectedPmIds, currentPageUnassignedIds,
            assignedCount, unassignedCount } = this.state;

        if (!id) return <div />;
        if (loading) return <LoadingSpinner />;

        const isAllSelected = currentPageUnassignedIds.length > 0 &&
            currentPageUnassignedIds.every(pmId => selectedPmIds.includes(pmId));

        const columns = [
            {
                Header: t('Manager'),
                id: 'pm_name',
                textAlign: 'start',
                accessor: 'pm_name',
                isSortable: true,
                Cell: ({ row }) => (
                    <div className="text-start">
                        {row.original.pm_name}
                        {row.original.email && <span className="text-muted ms-2">{row.original.email}</span>}
                    </div>
                )
            },
            {
                Header: appliedFilters.status === 'assigned' ? t('Unassign') : appliedFilters.status === 'unassigned' ? t('Assign') : `${t('Assign')} / ${t('Unassign')}`,
                id: 'assigned',
                textAlign: 'center',
                accessor: 'id',
                Cell: (cellProps) => this.renderCheckboxCell(cellProps.row),
            },
        ];

        return (
            <div>
                <div className="table-list mb-3">
                    <GenericList
                        key={this.state.reloadKey}
                        columns={columns}
                        stickyHeader
                        header={
                            <>
                                <Row className="align-items-center gx-2">
                                    <Col xs={12} lg={8} xl={8}>
                                        <Row className="member_status__summary">
                                            <Col xs="auto" className="mt-2 mt-sm-0 d-flex justify-content-center justify-content-sm-start">
                                                <div
                                                    className="highlightedgreen" role="button"
                                                    onClick={() => this.updateStatusFilter('assigned')}
                                                    style={{
                                                        cursor: 'pointer',
                                                        opacity: appliedFilters.status === 'assigned' ? 0.4 : 1
                                                    }}
                                                >
                                                    {`${t('Assigned')} ${t('Managers')}`}: {assignedCount}
                                                </div>
                                            </Col>
                                            <Col xs="auto" className="mt-2 mt-sm-0 d-flex justify-content-center">
                                                <div
                                                    className="highlightedred" role="button"
                                                    onClick={() => this.updateStatusFilter('unassigned')}
                                                    style={{
                                                        cursor: 'pointer',
                                                        opacity: appliedFilters.status === 'unassigned' ? 0.4 : 1
                                                    }}
                                                >
                                                    {`${t('Unassigned')} ${t('Managers')}`}: {unassignedCount}
                                                </div>
                                            </Col>
                                            <Col xs="auto" className="mt-2 mt-sm-0 d-flex justify-content-center justify-content-sm-end">
                                                <Checkbox
                                                    inlineBlock
                                                    isChecked={isAllSelected}
                                                    onChange={this.toggleSelectAll}
                                                    disabled={currentPageUnassignedIds.length === 0}
                                                    label={t("Select All")}
                                                />
                                            </Col>
                                        </Row>
                                    </Col>

                                    <Col xs="auto" className="d-flex justify-content-center justify-content-lg-end mt-2 mt-lg-0">
                                        {selectedPmIds.length > 0 && (
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip id="tooltip-assign-pm">{t('Assign')}</Tooltip>}
                                            >
                                                <Button
                                                    onClick={this.assignSelectedPms}
                                                    title={t('Assign')}
                                                    fill
                                                >
                                                    {t('Assign')} ({selectedPmIds.length})
                                                </Button>
                                            </OverlayTrigger>
                                        )}
                                    </Col>
                                </Row>
                                <GenericFilter
                                    filters={filters}
                                    setFilter={(name, value) => this.setFilter(name, value)}
                                    onFilter={() => this.onFilter()}
                                    loading={loading}
                                    hasStatusFilter={true}
                                    statusOptions={pm_assignment_statuses}
                                    searchFilterPlaceholder={`${t('Search')} ${t('Manager')}`}
                                />
                            </>
                        }
                        requestData={(params) => this.fetchPms(params)}
                        filters={this.state.appliedFilters}
                        page={this.state.page}
                        isPageAddedOrEdited={this.state.isPageAddedOrEdited}
                        handlePageChange={this.handlePageChange}
                        showPageSizeSelector
                        pageSizeListId="user_pm_assign"
                    />
                </div>
            </div>
        );
    }
}

export default inject('userStore', 'commonStore')(withLocalization(observer(UserPmAssignForm)));
